import { useMemo } from 'react';
import type { AxiosError } from 'axios';
import { ethers } from 'ethers';
import type { SWRConfiguration } from 'swr';
import useSWR from 'swr';

import { ERC20_ABI } from 'src/constants/abi';
import type { ERC20ContractBalanceOfPayload } from 'src/types/ethereum/contract';

import { useCurrentAccount } from './useAccount';
import { useCurrentWallet } from './useCurrentWallet';
import useProvider from './useProivder';

type FetchParams = {
  address: string;
  tokenContractAddress: string;
};

type UseTokenBalanceSWR = {
  tokenContractAddress: string;
  address?: string;
};

export function useTokenBalanceSWR(props?: UseTokenBalanceSWR, config?: SWRConfiguration) {
  const { tokenContractAddress, address } = props || {};
  const { currentAccount } = useCurrentAccount();
  const { currentWallet } = useCurrentWallet();

  const { provider } = useProvider();

  const accAddress = useMemo(() => address || currentAccount.accAddress, [address, currentAccount.accAddress]);

  const fetcher = async (params: FetchParams) => {
    const erc20Contract = new ethers.Contract(params.tokenContractAddress, ERC20_ABI, provider);

    return erc20Contract.balanceOf(params.address) as Promise<ERC20ContractBalanceOfPayload>;
  };

  const { data, isValidating, error, mutate } = useSWR<ERC20ContractBalanceOfPayload, AxiosError>(
    { id: 'tokenBalance', address: accAddress, tokenContractAddress },
    fetcher,
    {
      dedupingInterval: 9000,
      refreshInterval: 10000,
      errorRetryCount: 0,
      isPaused: () => !accAddress || !provider || !tokenContractAddress || !currentWallet.name,
      ...config,
    },
  );

  return { data, isValidating, error, mutate };
}
